/** MUTUAL FUND RESEARCH REPORTS. */
import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { COMPARE_SECURITIES, MF_REPORTS, MF_RESEARCH_PANELS } from '../queries';
import { EMPTY } from './_shared';

/** The report template app_admin generates and reads. */
export const RESEARCH_ARCHETYPE = 'GENERAL';

/**
 * Published research for one fund, or the whole catalogue when no symbol is
 * given.
 *
 * Two sources, deliberately independent: the report *list* (fund, when it was
 * generated, whether it is verified and complete, and the published URL) and
 * the report *content* as panels. Keeping them apart means the published report
 * stays reachable even when the content query cannot answer — which is how this
 * was written, back when that query failed outright. It answers now.
 *
 * The three framework scores come back null on every fund the panel query has
 * been asked for, so `scores` is routinely empty while the verdict fields
 * beside them are populated. That is why the modal leads with the
 * recommendation rather than the scores: the scores are the optional part.
 */
export function useFundResearch(securitySymbol, { limit = 25, skip = false, history = false } = {}) {
  /* Reports are written per scheme; a model portfolio has none, so the
     deep-dive skips both calls rather than asking twice for nothing. */
  const off = skip || !securitySymbol;
  const list = useQuery(MF_REPORTS, {
    variables: {
      securitySymbols: securitySymbol ? [securitySymbol] : null,
      archetype: RESEARCH_ARCHETYPE,
      limit,
      /**
       * Opt-in, per caller, and deliberately NOT the default.
       *
       * One fund's record is its editions — twelve or twenty on a scheme that
       * has been re-reviewed — which is what the deep-dive wants. The
       * catalogue wants the opposite: one current row per fund. Asked for
       * everything, a 25-row catalogue page returns 23 funds instead of 25,
       * with one scheme appearing three times and superseded editions pushing
       * current ones off the end.
       */
      includeHistory: history,
    },
    skip: off,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const content = useQuery(MF_RESEARCH_PANELS, {
    variables: { securitySymbol, archetype: RESEARCH_ARCHETYPE },
    skip: off,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* Both reads again, past the cache — for a caller that has just had a new
     edition written. */
  const listRefetch = list.refetch;
  const contentRefetch = content.refetch;
  const refetch = useCallback(
    () => Promise.allSettled([listRefetch(), contentRefetch()]),
    [listRefetch, contentRefetch],
  );

  return useMemo(() => {
    const res = list.data?.getMutualFundReports ?? null;
    const items = res?.items ?? EMPTY;
    const payload = content.data?.getSecuritySymbolsResearchData ?? null;
    const header = payload?.mfPanels?.headerPanel ?? null;

    /* The resolver reports its own failure in `error` as well as throwing, so
       surface whichever came back rather than showing an empty panel. */
    const contentError =
      content.error?.message
      || (payload && payload.success === false ? payload.error : null)
      || null;

    /* The report the screen opens: newest published, or the only one there is. */
    const latest = items.find((r) => r.isLatest) ?? items[0] ?? null;

    /**
     * Every edition, newest first, with the current one removed.
     *
     * Sorted here rather than trusted from the resolver, and filtered to the
     * ones that can actually be opened: a fund's older editions frequently
     * carry no `reportUrl` at all — eight of twenty on one scheme — and a row
     * that cannot open anything is a dead link, not a version.
     *
     * Formats differ across the record: the current edition is a PDF while the
     * superseded ones are HTML, so whatever opens them cannot assume either.
     */
    const history = items
      .filter((r) => r !== latest && r.reportUrl)
      .sort((a, b) => String(b.generatedAt || '').localeCompare(String(a.generatedAt || '')));

    /* How many editions exist at all, openable or not — `total` disagrees with
       the list on some schemes (24 claimed, 14 returned), so the rows we have
       are what gets counted. */
    const editions = items.length;

    return {
      loading: list.loading,
      error: list.error,
      refetch,
      reports: items,
      total: res?.total ?? 0,
      latest,
      history,
      editions,
      /**
       * Whether there is anything to show — a published report, or a verdict
       * the panel query answered with. Research covers 32 of the 1,417 schemes
       * in the master, so the screen asks this before offering to open it.
       */
      hasReport: Boolean(latest || header?.recommendationAction),
      contentLoading: content.loading,
      contentError,
      header,
      /**
       * People / Process / Performance and the overall placing — the framework
       * the report is organised around.
       *
       * These are letter grades ("A", "B", "C"), not numbers: the backend types
       * all four as `String`. Anything numeric applied to them — formatting,
       * comparison, a finite check — silently turns a grade into nothing.
       *
       * Always all four, graded or not. A missing grade is information: it says
       * which part of the framework has not been filled in, which is invisible
       * if the heading disappears with the value.
       */
      scores: [
        { label: 'People', value: header?.peopleScore ?? null },
        { label: 'Process', value: header?.processScore ?? null },
        { label: 'Performance', value: header?.performanceScore ?? null },
        { label: 'Overall', value: header?.overallRanking ?? null },
      ],
    };
  }, [list, content, refetch]);
}

/**
 * `SecurityTypeEnum` for a symbol, as `compareSecurities` needs it.
 *
 * Three values only — `mf`, `etf`, `stock` — and the enum is explicit that a
 * fund is `mf`, never `mutual_fund`. Passing the wrong one is answered "No
 * research found", which reads as an empty database rather than a bad
 * argument, so the mapping is made here once instead of at each call site.
 *
 * Scheme symbols on this desk end `.IN` and equities `.NSE`/`.BSE`; anything
 * else is left as a fund, which is what the comparison screens are for.
 */
export const compareEntityType = (symbol, kind) => {
  if (kind === 'stock') return 'stock';
  if (kind === 'etf') return 'etf';
  return /\.(NSE|BSE)$/i.test(String(symbol || '')) ? 'stock' : 'mf';
};

/** The horizons the head-to-head is judged over, as the resolver names them. */
export const COMPARE_HORIZONS = [
  ['short', 'Short · under 3y'],
  ['medium', 'Medium · 3–7y'],
  ['long', 'Long · 7y+'],
];

/**
 * The backend's head-to-head for two securities.
 *
 * Skipped until both symbols are known. A missing panel is not an error — see
 * the document — so `available` is the resolver's own `success`, and `error`
 * carries its words rather than any invented here.
 */
export function useSecurityComparison(symbolA, symbolB, { horizon = 'long', archetype = null, kindA, kindB } = {}) {
  const { data, loading, error } = useQuery(COMPARE_SECURITIES, {
    variables: {
      symbolA,
      symbolB,
      entityTypeA: compareEntityType(symbolA, kindA),
      entityTypeB: compareEntityType(symbolB, kindB),
      horizon,
      archetype,
    },
    skip: !symbolA || !symbolB,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const r = data?.compareSecurities ?? null;
    return {
      loading,
      error,
      available: Boolean(r?.success),
      /* The resolver's own message — only it knows which side it could not find. */
      reason: r?.error ?? null,
      comparison: r?.comparison ?? null,
      panelA: r?.panelA ?? null,
      panelB: r?.panelB ?? null,
      dimensions: r?.comparison?.dimensionScores ?? EMPTY,
    };
  }, [data, loading, error]);
}
